import React, { useState, useEffect } from "react";
import {
    Paper,
    Typography,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Tabs,
    Tab,
    Box,
    CircularProgress,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Avatar,
    IconButton,
    Menu,
    MenuItem as MuiMenuItem,
    TableSortLabel
} from "@material-ui/core";
import { Assessment, TrendingUp, ShoppingCart, Timeline, AccessTime, MoreVert, GetApp } from "@material-ui/icons";
import { toast } from "react-toastify";
import Chart from "react-apexcharts";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MetricCard from "../../components/MetricCard";
import { getFunnels } from "../../services/funnels";
import * as ReportsService from "../../services/reports";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(2),
        overflowY: "auto"
    },
    filters: {
        marginBottom: theme.spacing(3),
        display: "flex",
        gap: theme.spacing(2),
        alignItems: "center"
    },
    metricsGrid: {
        marginTop: theme.spacing(3)
    },
    card: {
        background: "#ffffff",
        borderRadius: "8px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
        padding: theme.spacing(2),
        marginBottom: theme.spacing(3)
    },
    cardHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: theme.spacing(2)
    },
    searchContainer: {
        background: "#f8f9fa", // table-light color assumption
        borderRadius: "4px",
        padding: "8px"
    },
    rankAvatar: {
        width: theme.spacing(6),
        height: theme.spacing(6),
        marginRight: theme.spacing(2)
    },
    userCell: {
        display: "flex",
        alignItems: "center"
    }
}));

const KanbanReports = () => {
    const classes = useStyles();
    const [funnels, setFunnels] = useState([]);
    const [selectedFunnelId, setSelectedFunnelId] = useState(null);
    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(false);

    // Dados dos relatórios
    const [conversionData, setConversionData] = useState(null);
    const [performanceData, setPerformanceData] = useState(null);
    const [productData, setProductData] = useState(null);
    const [originData, setOriginData] = useState(null);
    const [timeData, setTimeData] = useState(null);
    const [rankingData, setRankingData] = useState(null);
    const [leadsData, setLeadsData] = useState(null);

    useEffect(() => {
        const loadFunnels = async () => {
            try {
                const { data } = await getFunnels();
                setFunnels(data);
                if (data.length > 0) {
                    setSelectedFunnelId(data[0].id);
                }
            } catch (err) {
                toastError(err);
            }
        };
        loadFunnels();
    }, []);

    const handleGenerateReport = async () => {
        if (!selectedFunnelId) {
            toast.error("Selecione um funil");
            return;
        }

        setLoading(true);
        try {
            const [conversion, performance, product, origin, time, ranking, leads] = await Promise.all([
                ReportsService.getConversionReport(selectedFunnelId),
                ReportsService.getPerformanceReport(selectedFunnelId),
                ReportsService.getProductReport(selectedFunnelId),
                ReportsService.getOriginReport(selectedFunnelId),
                ReportsService.getTimeReport(selectedFunnelId),
                ReportsService.getRankingReport(selectedFunnelId),
                ReportsService.getLeadsReport(selectedFunnelId)
            ]);

            setConversionData(conversion.data);
            setPerformanceData(performance.data);
            setProductData(product.data);
            setOriginData(origin.data);
            setTimeData(time.data);
            setRankingData(ranking.data);
            setLeadsData(leads.data);

            toast.success("Relatórios gerados com sucesso!");
        } catch (err) {
            toastError(err);
        } finally {
            setLoading(false);
        }
    };

    const renderConversionTab = () => (
        <Box>
            {conversionData && (
                <Grid container spacing={3} className={classes.metricsGrid}>
                    <Grid item xs={12} sm={6} md={3}>
                        <MetricCard
                            icon={<TrendingUp />}
                            value={`${conversionData.conversionRate}%`}
                            label="Taxa de Conversão"
                            color="#4caf50"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MetricCard
                            icon={<AccessTime />}
                            value={`${conversionData.averageTimeInFunnel} dias`}
                            label="Tempo Médio no Funil"
                            color="#2196f3"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MetricCard
                            icon={<Assessment />}
                            value={conversionData.wonCount}
                            label="Oportunidades Ganhas"
                            color="#4caf50"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MetricCard
                            icon={<Assessment />}
                            value={conversionData.lostCount}
                            label="Oportunidades Perdidas"
                            color="#f44336"
                        />
                    </Grid>
                </Grid>
            )}
        </Box>
    );

    const renderPerformanceTab = () => (
        <Box>
            {performanceData && (
                <Grid container spacing={3} className={classes.metricsGrid}>
                    <Grid item xs={12} sm={6} md={3}>
                        <MetricCard
                            icon={<Assessment />}
                            value={performanceData.totalOpportunities}
                            label="Total de Oportunidades"
                            color="#3f51b5"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MetricCard
                            icon={<ShoppingCart />}
                            value={`R$ ${performanceData.totalValue.toLocaleString('pt-BR')}`}
                            label="Valor Total"
                            color="#4caf50"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MetricCard
                            icon={<Timeline />}
                            value={`R$ ${performanceData.averageTicket.toLocaleString('pt-BR')}`}
                            label="Ticket Médio"
                            color="#ff9800"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MetricCard
                            icon={<Assessment />}
                            value={performanceData.activeCards}
                            label="Cards Ativos"
                            color="#9c27b0"
                        />
                    </Grid>
                </Grid>
            )}
            {renderRankingSection()}
            {renderLeadsList()}
        </Box>
    );

    const renderRankingSection = () => {
        if (!rankingData || rankingData.length === 0) return null;

        const getChartOptions = (percent, color) => ({
            chart: {
                type: 'radialBar',
                height: 61,
                width: 60,
                sparkline: { enabled: true }
            },
            plotOptions: {
                radialBar: {
                    hollow: { size: '40%' },
                    track: { background: "rgba(0,0,0,0.05)" },
                    dataLabels: {
                        name: { show: false },
                        value: {
                            show: true,
                            fontSize: '10px',
                            offsetY: 4,
                            formatter: (val) => `${val}%`
                        }
                    }
                }
            },
            colors: [color],
            fill: { opacity: 1 },
            stroke: { lineCap: 'round' },
        });

        return (
            <Paper className={classes.card} style={{ marginTop: 24 }}>
                <Box className={classes.cardHeader}>
                    <Typography variant="h6">Performance da Equipe</Typography>
                </Box>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell align="center">Conversão</TableCell>
                                <TableCell align="center">Ganhos</TableCell>
                                <TableCell align="center">Perdidos</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rankingData.map((user) => (
                                <TableRow key={user.userId}>
                                    <TableCell>
                                        <Box className={classes.userCell}>
                                            <Avatar className={classes.rankAvatar}>{user.name.charAt(0)}</Avatar>
                                            <Box>
                                                <Typography variant="subtitle2">{user.name}</Typography>
                                                <Typography variant="caption" color="textSecondary">Usuário</Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box display="flex" justifyContent="center">
                                            <Chart
                                                options={getChartOptions(user.conversionRate, "#2196f3")}
                                                series={[user.conversionRate]}
                                                type="radialBar"
                                                width={60}
                                                height={61}
                                            />
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">{user.won}</TableCell>
                                    <TableCell align="center">{user.lost}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        );
    };

    const exportToCSV = () => {
        if (!leadsData) return;
        const headers = ["Nome", "Telefone", "Fase do Funil", "Valor", "Dias na Etapa", "Temperatura", "Negociador", "Data Criação"];

        // Calculate Totals for Footer
        const totalOpportunities = leadsData.length;
        const totalValue = leadsData.reduce((sum, lead) => sum + lead.value, 0);
        const averageTicket = totalOpportunities > 0 ? totalValue / totalOpportunities : 0;

        const rows = leadsData.map(lead => {
            // Translate Temperature
            let tempTranslated = lead.temperature;
            if (lead.temperature === 'hot') tempTranslated = 'Quente';
            if (lead.temperature === 'warm') tempTranslated = 'Morno';
            if (lead.temperature === 'cold') tempTranslated = 'Frio';

            // Format Value
            const valueFormatted = lead.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 });

            return [
                lead.contactName,
                lead.contactNumber,
                lead.funnelCardName,
                valueFormatted,
                lead.daysInStage,
                tempTranslated,
                lead.userName,
                new Date(lead.createdAt).toLocaleDateString()
            ];
        });

        // Add Footer Row
        rows.push([
            "TOTAIS",
            `Oportunidades: ${totalOpportunities}`,
            "",
            totalValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 }),
            "",
            "",
            `Ticket Médio: ${averageTicket.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
            ""
        ]);

        const csvContent = "data:text/csv;charset=utf-8,\uFEFF" // Add BOM for Excel utf-8
            + [headers.join(";"), ...rows.map(e => e.join(";"))].join("\n"); // Use semicolon for Excel Brazil

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "leads_report.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const handleRequestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedLeadsData = React.useMemo(() => {
        if (!leadsData) return null;
        let sortableItems = [...leadsData];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                // Special handling for temperature
                if (sortConfig.key === 'temperature') {
                    const tempOrder = { 'hot': 3, 'warm': 2, 'cold': 1 };
                    aValue = tempOrder[aValue] || 0;
                    bValue = tempOrder[bValue] || 0;
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [leadsData, sortConfig]);

    const renderLeadsList = () => {
        if (!leadsData) return null;

        // Calculate Totals
        const totalOpportunities = leadsData.length;
        const totalValue = leadsData.reduce((sum, lead) => sum + lead.value, 0);
        const averageTicket = totalOpportunities > 0 ? totalValue / totalOpportunities : 0;

        return (
            <Paper className={classes.card}>
                <Box className={classes.cardHeader}>
                    <Typography variant="h6">Lista de Leads</Typography>
                    <Button
                        variant="outlined"
                        startIcon={<GetApp />}
                        onClick={exportToCSV}
                    >
                        Exportar Excel (CSV)
                    </Button>
                </Box>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <TableSortLabel
                                        active={sortConfig.key === 'contactName'}
                                        direction={sortConfig.key === 'contactName' ? sortConfig.direction : 'asc'}
                                        onClick={() => handleRequestSort('contactName')}
                                    >
                                        Nome
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={sortConfig.key === 'funnelCardName'}
                                        direction={sortConfig.key === 'funnelCardName' ? sortConfig.direction : 'asc'}
                                        onClick={() => handleRequestSort('funnelCardName')}
                                    >
                                        Fase
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={sortConfig.key === 'value'}
                                        direction={sortConfig.key === 'value' ? sortConfig.direction : 'asc'}
                                        onClick={() => handleRequestSort('value')}
                                    >
                                        Valor
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={sortConfig.key === 'daysInStage'}
                                        direction={sortConfig.key === 'daysInStage' ? sortConfig.direction : 'asc'}
                                        onClick={() => handleRequestSort('daysInStage')}
                                    >
                                        Dias
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={sortConfig.key === 'temperature'}
                                        direction={sortConfig.key === 'temperature' ? sortConfig.direction : 'asc'}
                                        onClick={() => handleRequestSort('temperature')}
                                    >
                                        Temp.
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={sortConfig.key === 'userName'}
                                        direction={sortConfig.key === 'userName' ? sortConfig.direction : 'asc'}
                                        onClick={() => handleRequestSort('userName')}
                                    >
                                        Negociador
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={sortConfig.key === 'createdAt'}
                                        direction={sortConfig.key === 'createdAt' ? sortConfig.direction : 'asc'}
                                        onClick={() => handleRequestSort('createdAt')}
                                    >
                                        Data
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedLeadsData.map((lead) => (
                                <TableRow key={lead.opportunityId}>
                                    <TableCell>
                                        <Typography variant="body2">{lead.contactName}</Typography>
                                        <Typography variant="caption" color="textSecondary">{lead.contactNumber}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <span style={{
                                            backgroundColor: lead.funnelCardColor,
                                            padding: "2px 8px",
                                            borderRadius: "4px",
                                            color: "#fff",
                                            fontSize: "12px"
                                        }}>
                                            {lead.funnelCardName}
                                        </span>
                                    </TableCell>
                                    <TableCell>R$ {lead.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</TableCell>
                                    <TableCell>{lead.daysInStage}</TableCell>
                                    <TableCell>
                                        {lead.temperature === 'hot' && '🔥 Quente'}
                                        {lead.temperature === 'warm' && '⚠️ Morno'}
                                        {lead.temperature === 'cold' && '❄️ Frio'}
                                    </TableCell>
                                    <TableCell>{lead.userName}</TableCell>
                                    <TableCell>{new Date(lead.createdAt).toLocaleDateString()}</TableCell>
                                </TableRow>
                            ))}
                            <TableRow style={{ backgroundColor: "#f5f5f5" }}>
                                <TableCell colSpan={2} align="right">
                                    <Typography variant="subtitle2">Totais:</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="subtitle2">
                                        R$ {totalValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </Typography>
                                </TableCell>
                                <TableCell colSpan={4}>
                                    <Box display="flex" gap={2}>
                                        <Typography variant="caption" color="textSecondary">
                                            Oportunidades: <strong>{totalOpportunities}</strong>
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            Ticket Médio: <strong>R$ {averageTicket.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong>
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        );
    };

    return (
        <MainContainer>
            <MainHeader>
                <Title>📊 Relatórios do Kanban</Title>
            </MainHeader>

            <Paper className={classes.mainPaper} variant="outlined">
                <Box className={classes.filters}>
                    <FormControl variant="outlined" style={{ minWidth: 250 }} size="small">
                        <InputLabel>Funil</InputLabel>
                        <Select
                            value={selectedFunnelId || ""}
                            onChange={(e) => setSelectedFunnelId(e.target.value)}
                            label="Funil"
                        >
                            {funnels.map((funnel) => (
                                <MenuItem key={funnel.id} value={funnel.id}>
                                    {funnel.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleGenerateReport}
                        disabled={loading || !selectedFunnelId}
                    >
                        {loading ? <CircularProgress size={24} /> : "Gerar Relatório"}
                    </Button>
                </Box>

                <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                    <Tab label="Conversão" />
                    <Tab label="Performance" />
                    <Tab label="Produtos" disabled />
                    <Tab label="Origem" disabled />
                    <Tab label="Tempo" disabled />
                </Tabs>

                <Box mt={3}>
                    {tabValue === 0 && renderConversionTab()}
                    {tabValue === 1 && renderPerformanceTab()}
                </Box>
            </Paper>
        </MainContainer>
    );
};

export default KanbanReports;
